import "./styles/global.scss"
import "smartbanner.js/dist/smartbanner.min.css"

import { useEffect, useState } from "react"
import ReactDOM from "react-dom/client"
import { Provider } from "react-redux"
import { RouterProvider } from "react-router-dom"

import { AppErrorBoundary } from "@/components/common/AppErrorBoundary/AppErrorBoundary"
import { SplashScreenProvider } from "@/components/SplashScreen/SplashScreenProvider"
import SplashScreen from "@/pages/SplashScreen/SplashScreen"

import { isBillingPathname } from "./lib/billingCheckoutReturn"
import { initPromise, syncSmartBanner } from "./lib/i18n"
import { logger } from "./lib/logger"
import { router } from "./routes"
import { store } from "./store"

const suppressAppBanners = () => {
  document
    .querySelectorAll(
      'meta[name="apple-itunes-app"], meta[name^="smartbanner:"]',
    )
    .forEach((meta) => meta.remove())
  document.querySelector(".js_smartbanner")?.remove()

  const originalMargin = document.documentElement.getAttribute(
    "data-smartbanner-original-margin-top",
  )
  if (originalMargin !== null) {
    document.documentElement.style.marginTop = `${originalMargin}px`
    document.documentElement.removeAttribute(
      "data-smartbanner-original-margin-top",
    )
  }
}

const syncAppBanners = () => {
  if (isBillingPathname(window.location.pathname)) {
    suppressAppBanners()
    return
  }

  const bannerMeta = document.getElementById("app-banner-meta")
  if (
    bannerMeta instanceof HTMLTemplateElement &&
    !document.querySelector('meta[name="smartbanner:title"]')
  ) {
    document.head.appendChild(bannerMeta.content.cloneNode(true))
    syncSmartBanner()
  }

  // @ts-expect-error
  void import("smartbanner.js/src/smartbanner.js")
    .then(({ default: SmartBanner }) => {
      const publish = () => {
        if (
          isBillingPathname(window.location.pathname) ||
          document.querySelector(".js_smartbanner")
        ) {
          return
        }
        syncSmartBanner()
        new SmartBanner().publish()
      }

      if (document.readyState === "complete") {
        publish()
      } else {
        window.addEventListener("load", publish, { once: true })
      }
    })
    .catch((error) => logger.warn("App banner initialization failed", error))
}

syncAppBanners()

export const App = () => {
  const [i18nReady, setI18nReady] = useState(false)

  useEffect(() => {
    return router.subscribe(syncAppBanners)
  }, [])

  useEffect(() => {
    void initPromise
      .then(() => setI18nReady(true))
      .catch((error) => {
        logger.error("i18n init failed, proceeding with defaults", error)
        setI18nReady(true)
      })
  }, [])

  return (
    <SplashScreenProvider>
      <SplashScreen />
      {i18nReady && (
        <Provider store={store}>
          <AppErrorBoundary>
            <RouterProvider router={router} />
          </AppErrorBoundary>
        </Provider>
      )}
    </SplashScreenProvider>
  )
}

ReactDOM.createRoot(document.getElementById("root")!).render(<App />)
